import React from 'react';
import classNames from 'classnames';
import { Icon, Tooltip } from 'Components';
import styles from './OverviewCard.module.scss';

/**
 * Tints the icon tile and the rating badge together, so a card reads as one
 * colour story. Callers map their own thresholds onto a tone.
 */
export enum OverviewCardTone {
	Info = 'info',
	Success = 'success',
	Neutral = 'neutral',
	Warning = 'warning',
	Danger = 'danger'
}

export type OverviewCardVariant = 'stacked' | 'inline';

export interface OverviewCardProps {
	value: string | number;
	label: string;
	icon: React.ReactNode;
	description?: string;
	tooltip?: string;
	/**
	 * `stacked` (default) puts the value above the label and description.
	 * `inline` pairs the icon with the label on one row and the value with an
	 * optional rating badge on the next — the reporting KPI layout.
	 */
	variant?: OverviewCardVariant;
	tone?: OverviewCardTone;
	/** Rating shown beside the value, e.g. "Excellent". `inline` only. */
	badge?: string;
	className?: string;
}

const OverviewCard: React.FC<OverviewCardProps> = ({
	value,
	label,
	description,
	icon,
	tooltip,
	variant = 'stacked',
	tone = OverviewCardTone.Neutral,
	badge,
	className
}) => {
	const infoIcon = tooltip ? (
		<Tooltip
			title={tooltip}
			// The KPI layout opens its tooltip below the icon, per the design.
			placement={variant === 'inline' ? 'bottomRight' : 'topRight'}
			overlayClassName={styles.tooltip}
		>
			<Icon
				type="infoTooltip"
				role="img"
				className={styles['info-icon']}
			/>
		</Tooltip>
	) : null;

	if (variant === 'inline') {
		return (
			<div
				className={classNames(
					styles.card,
					styles['card--inline'],
					className
				)}
			>
				<div className={styles.header}>
					<div className={styles.identity}>
						<span
							className={classNames(
								styles['icon-tile'],
								styles[`icon-tile--${tone}`]
							)}
						>
							{icon}
						</span>
						<span className={styles['inline-label']}>{label}</span>
					</div>
					{infoIcon}
				</div>

				<div className={styles['value-row']}>
					<span className={styles['inline-value']}>{value}</span>
					{badge && (
						<span
							className={classNames(
								styles.badge,
								styles[`badge--${tone}`]
							)}
						>
							{badge}
						</span>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className={classNames(styles.card, className)}>
			<div className={styles.header}>
				<span className={styles['status-icon']}>{icon}</span>
				{infoIcon}
			</div>
			<div className={styles.value}>{value}</div>
			<div className={styles.label}>{label}</div>
			{description && (
				<div className={styles.description}>{description}</div>
			)}
		</div>
	);
};

export default OverviewCard;
